import React from 'react';
import { User, LogIn, UserPlus, Trophy, Users, Calendar } from 'lucide-react';
import gafferIcon from '../assets/gaffer-head-icon.png';

// `short` is the bottom-bar label. Four tabs share the width of a phone, and "My" on
// two of them is a word nobody needs repeated once they are signed in.
const LOGGED_IN_LINKS = [
  { id: 'home', label: 'Home', short: 'Home', icon: Trophy },
  { id: 'my-games', label: 'My Games', short: 'Games', icon: Users },
  { id: 'rules', label: 'Rules', short: 'Rules', icon: Calendar },
  { id: 'profile', label: 'My Profile', short: 'Profile', icon: User },
];

const LOGGED_OUT_LINKS = [
  { id: 'home', label: 'Home', icon: Trophy },
  { id: 'login', label: 'Login', icon: LogIn },
  { id: 'register', label: 'Register', icon: UserPlus, className: 'register-btn' },
];

function linkClass(link, currentPage) {
  const classes = ['nav-link'];
  if (currentPage === link.id) classes.push('active');
  if (link.className) classes.push(link.className);
  return classes.join(' ');
}

function NavBar({ isLoggedIn, currentUser, currentPage, onNavigate, onLogout }) {
  const links = isLoggedIn ? LOGGED_IN_LINKS : LOGGED_OUT_LINKS;

  const navigate = (pageId) => onNavigate(pageId);
  const logout = () => onLogout();

  const greeting = `Welcome, ${currentUser?.firstName || 'Player'}!`;

  // The wordmark is live text, not baked into the icon, so it stays selectable
  // and picks up the gradient treatment the rest of the header uses.
  const brand = (
    <div className="nav-logo">
      <img className="logo-icon" src={gafferIcon} alt="" aria-hidden="true" />
      <span className="logo-text">Gaffer Picks</span>
    </div>
  );

  const renderLinks = () =>
    links.map((link) => {
      const Icon = link.icon;
      return (
        <button key={link.id} className={linkClass(link, currentPage)} onClick={() => navigate(link.id)}>
          <Icon size={18} />
          {link.label}
        </button>
      );
    });

  return (
    <>
      {/* Desktop and mobile navigation are both in the DOM, with CSS showing one or the
          other. They carry distinct accessible names so the hidden one is never mistaken
          for the live one - by a screen reader, or by a test, which sees no CSS at all. */}
      <nav className="nav-desktop" aria-label="Main">
        {brand}
        <div className="nav-links">
          {isLoggedIn && <span className="user-greeting">{greeting}</span>}
          {renderLinks()}
          {isLoggedIn && (
            <button className="nav-link logout" onClick={logout}>
              Log Out
            </button>
          )}
        </div>
      </nav>

      {/* Mobile top bar. Carries only identity and the way out - the destinations live
          in the bottom bar, within reach of a thumb. */}
      <nav className="nav-mobile" aria-label="Account">
        {brand}
        {isLoggedIn ? (
          <button className="nav-mobile-logout" onClick={logout}>Log Out</button>
        ) : (
          <div className="nav-mobile-auth">
            {LOGGED_OUT_LINKS.filter((l) => l.id !== 'home').map((link) => (
              <button key={link.id} className={linkClass(link, currentPage)} onClick={() => navigate(link.id)}>
                {link.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Mobile bottom bar.
          These five were behind a hamburger, which on the layout most players use put a
          tap of pure overhead in front of every destination - in an app whose whole loop
          is "open it, check whether I have picked". Signed out there are only three
          places to go and two of them are Login and Register, so the top bar says it all
          and no bar appears down here. */}
      {isLoggedIn && (
        <nav className="nav-bottom" aria-label="Sections">
          {LOGGED_IN_LINKS.map((link) => {
            const Icon = link.icon;
            const active = currentPage === link.id;
            return (
              <button
                key={link.id}
                className={`nav-tab${active ? ' active' : ''}`}
                aria-current={active ? 'page' : undefined}
                onClick={() => navigate(link.id)}
              >
                <Icon size={20} aria-hidden="true" />
                <span>{link.short}</span>
              </button>
            );
          })}
        </nav>
      )}
    </>
  );
}

export default NavBar;
